/* External */
import {
  memo, useEffect, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Assets */
import Logo from '~/assets/theme/svg/brand-logo.svg?react';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButton from '../FocusableButton';
import CustomFormattedMessage from '../CustomFormattedMessage';

/* Utils */
import { MessageId, MESSAGES } from './constants';
import { useLoading } from '../../providers/LoadingProvider';
import { useOnBackPress } from '../../utils/keyDownEvents';
import { noOp } from '../../utils/functions';

/* Styles */
import './FullScreenMessage.scss';

function FullScreenMessage({ messageKey, setFocus, realFocusKey }) {
  const brandLogo = getJsonImage('brand-logo');
  const buttonFocusKey = `${realFocusKey}_button`;
  const { hideLoading } = useLoading();
  const {
    title,
    description,
    button,
    action = noOp,
    backgroundImage,
  } = MESSAGES[messageKey] || {};
  const [hasBackgroundImage, setHasBackgroundImage] = useState(!!backgroundImage);

  useOnBackPress({ listener: action });

  useEffect(() => {
    hideLoading();
    if (button) {
      setFocus(buttonFocusKey);
    }
  }, [button, buttonFocusKey, hideLoading, setFocus]);

  const onErrorBackgroundImage = useCallback(() => {
    setHasBackgroundImage(false);
  }, []);

  return (
    <div className={`dtv-html5-c-full-screen-message ${hasBackgroundImage ? 'dtv-html5-c-full-screen-message--with-background-image' : ''}`}>
      {backgroundImage && hasBackgroundImage && (
        <img
          className="dtv-html5-c-full-screen-message__background-image"
          src={backgroundImage}
          alt=""
          onError={onErrorBackgroundImage}
        />
      )}
      {brandLogo ? (
        <img
          src={brandLogo}
          alt="logo"
          className="dtv-html5-c-full-screen-message__logo"
        />
      ) : (
        <Logo className="dtv-html5-c-full-screen-message__logo" />
      )}
      <div className="dtv-html5-c-full-screen-message__content-message">
        <h1 className="dtv-html5-c-full-screen-message__title">
          <CustomFormattedMessage message={title}>{title.children}</CustomFormattedMessage>
        </h1>
        {!!description && (
          <p className="dtv-html5-c-full-screen-message__description">
            <CustomFormattedMessage message={description}>
              {description.children}
            </CustomFormattedMessage>
          </p>
        )}
      </div>
      {!!button && (
        <div className="dtv-html5-c-full-screen-message__content-button">
          <FocusableButton
            onEnterPress={action}
            focusKey={buttonFocusKey}
            primary
          >
            <CustomFormattedMessage message={button}>{button.children}</CustomFormattedMessage>
          </FocusableButton>
        </div>
      )}
    </div>
  );
}

FullScreenMessage.propTypes = {
  messageKey: PropTypes.oneOf(Object.values(MessageId)).isRequired,
  setFocus: PropTypes.func.isRequired,
  realFocusKey: PropTypes.string.isRequired,
};

export default memo(withFocusable()(memo(FullScreenMessage)));
